import { formatPercent, formatPrice, formatTime } from '../../lib/format';
import type { CandidateRecord, CandidateZoneSummary } from '../../types/market';
import { Badge } from '../ui/Badge';
import { ScoreMeter } from '../ui/ScoreMeter';
import { DirectionBadge, FamilyBadge, StateBadge } from './CandidateBadges';

interface CandidateCardProps {
  candidate: CandidateRecord;
  mode: 'predictor' | 'monitoring';
}

function percentClass(value: number): string {
  return value > 0 ? 'metric-positive' : value < 0 ? 'metric-negative' : 'metric-muted';
}

function zoneLabel(zone?: CandidateZoneSummary): string {
  if (!zone) return 'Pending';
  return `$${formatPrice(zone.low)}–$${formatPrice(zone.high)}`;
}

export function CandidateCard({ candidate, mode }: CandidateCardProps): React.JSX.Element {
  const validationTone = candidate.validationStatus === 'complete'
    ? 'positive'
    : candidate.validationStatus === 'partial'
      ? 'info'
      : candidate.validationStatus === 'failed'
        ? 'negative'
        : candidate.baselineReady
          ? 'info'
          : 'warning';

  return (
    <a
      href={`#/coin/${candidate.symbol}`}
      className={`candidate-card candidate-card-${candidate.direction}`}
      aria-label={`Open ${candidate.symbol} deep analysis subpage`}
    >
      {/* 1. Header Section */}
      <header className="card-header">
        <div className="card-identity">
          <span className="coin-avatar">{candidate.baseAsset.slice(0, 2)}</span>
          <div className="card-title-group">
            <div className="card-symbol-row">
              <strong className="card-asset">{candidate.symbol}</strong>
            </div>
            <div className="card-meta-pills">
              <span className="meta-tag">{candidate.primaryExchange ?? 'public'}</span>
              <span className="meta-tag">{candidate.marketKind}</span>
              <span className="meta-tag horizon-tag">{candidate.horizon}</span>
            </div>
          </div>
        </div>

        <div className="card-badge-column">
          <DirectionBadge direction={candidate.direction} />
          <FamilyBadge family={candidate.family} />
        </div>
      </header>

      {/* 2. Validation & State Bar */}
      <div className="card-state-bar">
        <StateBadge state={candidate.state} />
        <Badge tone={validationTone}>
          {candidate.validationStatus === 'complete'
            ? 'deep complete'
            : candidate.validationStatus === 'partial'
              ? 'deep partial'
              : candidate.validationStatus === 'failed'
                ? 'deep failed'
                : candidate.validationStatus === 'queued'
                  ? 'deep queued'
                  : candidate.baselineReady
                    ? 'radar ready'
                    : 'warming up'}
        </Badge>
        <span className="card-coverage-note">
          {Math.round(candidate.sampleCoverageMs / 60_000)}m baseline · {Math.round(candidate.validationCoverage * 100)}% deep
        </span>
      </div>

      {/* 3. Price & Delta Performance Grid */}
      <div className="card-metrics-grid">
        <div className="card-metric-item">
          <span className="metric-label">Found Price</span>
          <span className="metric-val">${formatPrice(candidate.foundPrice)}</span>
          <span className={`metric-sub ${percentClass(candidate.foundChange24h)}`}>
            24h: {formatPercent(candidate.foundChange24h)}
          </span>
        </div>

        <div className="card-metric-item">
          <span className="metric-label">Current Price</span>
          <span className="metric-val">${formatPrice(candidate.currentPrice)}</span>
          <span className={`metric-sub ${percentClass(candidate.currentChange24h)}`}>
            24h: {formatPercent(candidate.currentChange24h)}
          </span>
        </div>

        <div className="card-metric-item card-metric-highlight">
          <span className="metric-label">Changed Since Found</span>
          <span className={`metric-val ${percentClass(candidate.changeSinceFound)}`}>
            {formatPercent(candidate.changeSinceFound)}
          </span>
          <span className="metric-sub text-soft">Post-discovery delta</span>
        </div>

        <div className="card-metric-item">
          <span className="metric-label">Thesis Range</span>
          <div className="thesis-range">
            <span className={percentClass(candidate.bestMove)}>Best {formatPercent(candidate.bestMove)}</span>
            <span className={percentClass(candidate.worstMove)}>Worst {formatPercent(candidate.worstMove)}</span>
          </div>
        </div>
      </div>

      {/* 4. Quality & Clock Meters */}
      <div className="card-meters-row">
        <ScoreMeter
          value={candidate.candidateScore}
          label={mode === 'predictor' ? 'Core score' : 'observation score'}
        />
        <ScoreMeter value={candidate.opportunityFreshness} label="freshness" />
        {candidate.deepValidated && (
          <div className="card-clock-meter">
            <div className="clock-header">
              <span className="clock-label">Independent support</span>
              <span className="clock-val">{candidate.supportingScore > 0 ? '+' : ''}{candidate.supportingScore}</span>
            </div>
            <span className="clock-sub">{candidate.supportingAnalysis?.supportiveCount ?? 0} supporting methods · separate from Core</span>
          </div>
        )}

        <div className="card-clock-meter">
          <div className="clock-header">
            <span className="clock-label">Candle Clock</span>
            <span className="clock-val">{candidate.candleMaturity.toFixed(0)}% mature</span>
          </div>
          <div className="clock-bar-track">
            <div className="clock-bar-fill" style={{ width: `${Math.min(100, Math.max(0, candidate.candleMaturity))}%` }} />
          </div>
          <span className="clock-sub">
            {candidate.freshIssuanceAllowed
              ? candidate.secondsToClose < 60
                ? `${Math.max(0, candidate.secondsToClose)}s to close`
                : `${Math.ceil(candidate.secondsToClose / 60)}m to close`
              : 'Transition block'}
          </span>
        </div>
      </div>

      {/* 5. Support / Resistance & Driver Section */}
      <div className="card-levels-driver">
        <div className="card-level-chip">
          <span className="level-label text-positive">Support</span>
          <span className="level-val">{zoneLabel(candidate.nearestSupport)}</span>
        </div>
        <div className="card-level-chip">
          <span className="level-label text-negative">Resistance</span>
          <span className="level-val">{zoneLabel(candidate.nearestResistance)}</span>
        </div>
      </div>

      <div className="card-driver-box">
        <div className="driver-head">
          <strong className="driver-title">{candidate.primaryDriver}</strong>
          <span className="driver-time">Updated {formatTime(candidate.lastUpdatedAt)}</span>
        </div>
        <p className="driver-desc">{candidate.contextSummary ?? candidate.statusNote}</p>
      </div>

      {/* 6. Action Target Footer */}
      <footer className="card-footer">
        <span className="card-action-text">Open Deep Analysis & Chart</span>
        <span className="card-action-arrow">→</span>
      </footer>
    </a>
  );
}
